import { useState, FormEvent } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const RevenueLossCalculator = () => {
  const navigate = useNavigate();
  const [avgValue, setAvgValue] = useState("");
  const [missedCalls, setMissedCalls] = useState("");
  const [timePeriod, setTimePeriod] = useState("month");
  const [showResults, setShowResults] = useState(false);
  
  // Calculate annual values
  const calculateAnnualLoss = (calls: number, value: number, period: string) => {
    const multiplier = period === "day" ? 365 : period === "week" ? 52 : 12;
    const annualCalls = calls * multiplier;
    const lostCalls = annualCalls * 0.85; // 85% never call back
    return lostCalls * value;
  };

  const handleCalculate = (e: FormEvent) => {
    e.preventDefault();
    setShowResults(true);
    // Scroll to results
    setTimeout(() => {
      document.getElementById("calculator-results")?.scrollIntoView({ 
        behavior: "smooth", 
        block: "nearest" 
      });
    }, 100);
  };

  const annualLoss = avgValue && missedCalls ? calculateAnnualLoss(Number(missedCalls), Number(avgValue), timePeriod) : 0;
  const monthlyLoss = Math.round(annualLoss / 12);
  const missedCallsAnnual = missedCalls ? Number(missedCalls) * (timePeriod === "day" ? 365 : timePeriod === "week" ? 52 : 12) : 0;

  return (
    <section id="industry-calculators" className="py-8 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-red-900">
            Your Business is Losing Money Right Now
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            85% of customers whose calls you miss will NEVER call back. 
            Here's what that's costing YOUR industry every single month:
          </p>
        </div>

        {/* Interactive Calculator */}
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold mb-2">
              Want to See YOUR Exact Numbers?
            </h3>
            <p className="text-gray-600">
              Enter your specific details for a personalized calculation
            </p>
          </div>

          <Card className="border-2 border-blue-500 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
              <CardTitle>Custom Loss Calculator</CardTitle>
            </CardHeader>
            
            <CardContent className="pt-6">
              <form onSubmit={handleCalculate} className="space-y-6">
                {/* Input 1: Average Transaction Value */}
                <div>
                  <label htmlFor="avg-customer-value" className="block text-base font-medium mb-2">
                    Average Customer Value:
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-base text-gray-500">$</span>
                    <Input
                      id="avg-customer-value"
                      type="number"
                      min="1"
                      step="1"
                      value={avgValue}
                      onChange={(e) => setAvgValue(e.target.value)}
                      className="pl-8"
                      placeholder="e.g., 500"
                      required
                    />
                  </div>
                  <p className="text-base text-gray-500 mt-1">
                    Average sale/service/appointment value
                  </p>
                </div>

                {/* Input 2: Missed Calls Per Period */}
                <div>
                  <label htmlFor="missed-calls" className="block text-base font-medium mb-2">
                    How many calls/inquiries do you estimate you miss:
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Input
                        id="missed-calls"
                        type="number"
                        min="0"
                        value={missedCalls}
                        onChange={(e) => setMissedCalls(e.target.value)}
                        placeholder="e.g., 10"
                        required
                      />
                    </div>
                    <div>
                      <Select value={timePeriod} onValueChange={setTimePeriod}>
                        <SelectTrigger aria-label="Select time period">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="day">Per Day</SelectItem>
                          <SelectItem value="week">Per Week</SelectItem>
                          <SelectItem value="month">Per Month</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="text-base text-gray-500 mt-1">
                    Be conservative - this adds up fast
                  </p>
                </div>

                {/* Calculate Button */}
                <Button type="submit" className="w-full" size="lg">
                  Calculate My Losses
                </Button>
              </form>

              {/* Results Display (shows after calculate) */}
              {showResults && annualLoss > 0 && (
                <div id="calculator-results" className="mt-8 space-y-6 animate-fade-in">
                  {/* The Bad News */}
                  <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-600">
                    <h4 className="font-bold text-xl mb-3 text-red-900 flex items-center gap-2">
                      <AlertCircle className="text-red-600" />
                      💸 Your Current Losses:
                    </h4>
                    <div className="space-y-2">
                      <p className="text-lg">
                        {missedCallsAnnual.toLocaleString()} missed opportunities per year
                      </p>
                      <p className="text-lg">
                        × ${Number(avgValue).toLocaleString()} average value
                      </p>
                      <p className="text-lg">
                        × 85% who never call back
                      </p>
                      <div className="mt-4 pt-4 border-t-2 border-red-300">
                        <p className="text-base text-red-700">Annual Revenue Loss:</p>
                        <p className="text-4xl font-bold text-red-600">
                          ${annualLoss.toLocaleString()}
                        </p>
                        <p className="text-base text-red-700 mt-1">
                          That's ${monthlyLoss.toLocaleString()}/month walking out the door
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="text-center">
                    <Button size="lg" className="text-lg px-8" onClick={() => navigate("/dashboard")}>
                      Stop Losing ${monthlyLoss.toLocaleString()}/Month - Get Started
                    </Button>
                    <p className="text-base text-gray-500 mt-2">
                      Join businesses capturing 85% more of their opportunities
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};